import { type ReactNode, Suspense } from "react";
import { redirect, type RouteObject } from "react-router-dom";

import AppErrorDispatcher from "@/components/organisms/AppErrorDispatcher";
import { APP_MODULES, PAGE_URL } from "@/data_source/constants";
import { ENABLE_ASSET_PHOTO_GALLERY } from "@/data_source/constants/env";
import AppAccessNotConfigured from "@/pages/AppAccessNotConfigured";
import AppErrorPage from "@/pages/AppErrorPage";
import AppLoading from "@/pages/AppLoading";
import AppNotFoundPage from "@/pages/AppNotFoundPage";
import AppPasswordRecovery from "@/pages/AppPasswordRecovery";
import AppSignIn from "@/pages/AppSignIn";
import AppUserInactive from "@/pages/AppUserInactive";
import * as authValidator from "@/utils/auth";

import { getAssetMutationRouteAccessValidator } from "./assetMutationRouteAccess";
import {
  assetDetailAccessValidator,
  assetPhotoGalleryAccessValidator
} from "./assetRowActionAccess";
import * as pages from "./lazyLoadedComponents";

import type { RouterValidator } from "./router_guard";

export type RouteAppComponentProps = {
  pageName?: string;
  routeType?: "recovery" | "set";
};

function suspenseWrapper(
  Component: (props: RouteAppComponentProps) => JSX.Element | ReactNode | null
) {
  return (props: RouteAppComponentProps): JSX.Element => (
    <Suspense fallback={<AppLoading />}>
      {Component ? <Component {...props} /> : null}
    </Suspense>
  );
}
export type AppRouteSettings = RouteObject & {
  accessValidator: RouterValidator;
  children?: AppRouteSettings[];
};

const masterDashboardManageGuard =
  authValidator.capabilityGuardValidationBuilder(
    authValidator.CAPABILITIES.DASHBOARD.MANAGE,
    [APP_MODULES.SETTINGS_MODULE],
    ["master"]
  );

const routes: AppRouteSettings[] = [
  {
    path: PAGE_URL.SIGN_UP,
    element: suspenseWrapper(pages.AppSignUp)({ pageName: "SignUp" }),
    children: [],
    accessValidator: authValidator.noGuardValidationBuilder()
  },
  {
    path: PAGE_URL.SIGN_UP_CODE_CONFIRMATION,
    element: suspenseWrapper(pages.AppSignUpCodeConfirmation)({
      pageName: "Code Confirmation"
    }),
    children: [],
    accessValidator: authValidator.noGuardValidationBuilder()
  },
  {
    path: PAGE_URL.SIGN_UP_CHECKBOX,
    element: suspenseWrapper(pages.AppSignUpCheckbox)({
      pageName: "Checkbox Jobs"
    }),
    children: [],
    accessValidator: authValidator.noGuardValidationBuilder()
  },
  {
    path: PAGE_URL.SIGN_UP_PROJECT,
    element: suspenseWrapper(pages.AppSignUpProject)({
      pageName: "WorkspaceConfirmation"
    }),
    children: [],
    accessValidator: authValidator.noGuardValidationBuilder()
  },
  {
    path: PAGE_URL.SIGN_IN,
    element: suspenseWrapper(AppSignIn)({ pageName: "" }),
    accessValidator: authValidator.noGuardValidationBuilder()
  },
  {
    path: PAGE_URL.PASSWORD_RECOVERY,
    element: suspenseWrapper(AppPasswordRecovery)({
      pageName: "Recovery password",
      routeType: "recovery"
    }),
    accessValidator: authValidator.noGuardValidationBuilder()
  },
  {
    path: PAGE_URL.PASSWORD_SET,
    element: suspenseWrapper(AppPasswordRecovery)({
      pageName: "Set password",
      routeType: "set"
    }),
    accessValidator: authValidator.noGuardValidationBuilder()
  },
  {
    path: PAGE_URL.TIMESHEETS_KIOSK,
    element: suspenseWrapper(pages.AppTimesheetsKiosk)({
      pageName: "Timesheets Kiosk"
    }),
    accessValidator: authValidator.noGuardValidationBuilder()
  },
  {
    path: PAGE_URL.USER_INACTIVE,
    element: suspenseWrapper(AppUserInactive)({
      pageName: "User Inactive"
    }),
    accessValidator: authValidator.noGuardValidationBuilder()
  },
  {
    path: PAGE_URL.ACCESS_NOT_CONFIGURED,
    element: suspenseWrapper(AppAccessNotConfigured)({
      pageName: "Access Not Configured"
    }),
    accessValidator: authValidator.noGuardValidationBuilder()
  },
  {
    path: PAGE_URL.HOME,
    element: suspenseWrapper(pages.Home)({ pageName: "" }),
    accessValidator: authValidator.onlyPermissionUser(),
    children: [
      {
        path: PAGE_URL.ERROR_404,
        element: <AppNotFoundPage pageName="Not Found" />,
        accessValidator: authValidator.noGuardValidationBuilder()
      },
      {
        path: PAGE_URL.ERROR_PAGE,
        element: <AppErrorPage pageName="Error Page" />,
        accessValidator: authValidator.noGuardValidationBuilder()
      },
      {
        path: PAGE_URL.HOME,
        element: suspenseWrapper(pages.AppDashboardNew)({ pageName: "" }),
        // The dashboard renders the backend's specific denial reason itself;
        // its data requests remain gated by capability and dashboard_access.
        accessValidator: authValidator.noGuardValidationBuilder()
      },
      {
        path: PAGE_URL.DASHBOARD_TEMPLATE,
        element: suspenseWrapper(pages.AppDashboardTemplate)({
          pageName: "Dashboard Template"
        }),
        accessValidator: masterDashboardManageGuard
      },
      {
        path: PAGE_URL.LAYOUT_DASHBOARD_WIDGETS,
        element: suspenseWrapper(pages.AppDashboardTemplate)({
          pageName: "Dashboard Template"
        }),
        accessValidator: masterDashboardManageGuard
      },
      {
        path: PAGE_URL.LAYOUT_DASHBOARD_WIDGETS_NEW,
        element: suspenseWrapper(pages.AppDashboardTemplate)({
          pageName: "Dashboard Template"
        }),
        accessValidator: masterDashboardManageGuard
      },
      {
        path: PAGE_URL.POLL_NEW,
        element: suspenseWrapper(pages.AppPollBuilderV2)({
          pageName: "New Poll V2"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.FORMS.CREATE,
          [APP_MODULES.FORM_MODULE]
        )
      },
      {
        path: PAGE_URL.POLL_NEW_WITH_SCHEMA,
        element: suspenseWrapper(pages.AppPollBuilderV2)({
          pageName: "New Poll"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.FORMS.CREATE,
          [APP_MODULES.FORM_MODULE]
        )
      },
      {
        path: PAGE_URL.POLL_IMPORT,
        element: suspenseWrapper(pages.AppPollImport)({
          pageName: "Import Poll"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.FORMS.CREATE,
          [APP_MODULES.FORM_MODULE]
        )
      },
      {
        path: PAGE_URL.POLL_UPDATE,
        element: suspenseWrapper(pages.AppPollBuilderV2)({
          pageName: "Update Forms"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.FORMS.UPDATE,
          [APP_MODULES.FORM_MODULE]
        )
      },
      {
        path: PAGE_URL.POLL_DELETE,
        element: suspenseWrapper(pages.AppPollDelete)({
          pageName: "Delete Form"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.FORMS.ARCHIVE,
          [APP_MODULES.FORM_MODULE]
        )
      },
      {
        path: PAGE_URL.AUDIT_MANAGEMENT_DETAILS,
        element: suspenseWrapper(pages.AppAuditManagementDetailV2)({
          pageName: "Audit Details"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.FORM_RESPONSES.VIEW,
          [APP_MODULES.FORM_MODULE]
        )
      },
      {
        path: PAGE_URL.AUDIT_MANAGEMENT_UPDATE_ANSWER,
        element: suspenseWrapper(pages.AppAuditManagementAnswerUpdate)({
          pageName: "Update Poll Answer"
        }),
        accessValidator: authValidator.capabilitiesGuardValidationBuilder(
          [
            authValidator.CAPABILITIES.FORM_RESPONSES.VIEW,
            authValidator.CAPABILITIES.FORM_RESPONSES.MANAGE
          ],
          [APP_MODULES.FORM_MODULE],
          ["operational"],
          { requireAll: true }
        )
      },
      {
        path: PAGE_URL.POLL_ANSWER_DELETE,
        element: suspenseWrapper(pages.AppPollAnswerDelete)({
          pageName: "Poll Answer Delete"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.FORM_RESPONSES.MANAGE,
          [APP_MODULES.FORM_MODULE]
        )
      },
      {
        path: PAGE_URL.AUDIT_MANAGEMENT_NEW_ANSWER,
        element: suspenseWrapper(pages.AppAuditManagementAnswerNew)({
          pageName: "New Poll Answer"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.FORMS.EXECUTE,
          [APP_MODULES.FORM_MODULE]
        )
      },
      {
        path: PAGE_URL.AUDIT_MANAGEMENT,
        element: suspenseWrapper(pages.AppAuditManagement)({
          pageName: "Audit Management"
        }),
        accessValidator: authValidator.capabilitiesGuardValidationBuilder(
          [
            authValidator.CAPABILITIES.FORMS.VIEW,
            authValidator.CAPABILITIES.FORM_RESPONSES.VIEW,
            authValidator.CAPABILITIES.MEDIA_TEMPLATES.VIEW
          ],
          [APP_MODULES.FORM_MODULE, APP_MODULES.PICTURE_MODULE]
        ),
        children: [
          {
            path: "",
            element: suspenseWrapper(pages.AppAuditManagementResponse)({
              pageName: "Audit Management"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              authValidator.CAPABILITIES.FORM_RESPONSES.VIEW,
              [APP_MODULES.FORM_MODULE]
            ),
            children: [
              {
                path: PAGE_URL.AUDIT_MANAGEMENT_LIST,
                element: suspenseWrapper(pages.AppAuditManagementList)({
                  pageName: "Audit Management"
                }),
                accessValidator: authValidator.capabilityGuardValidationBuilder(
                  authValidator.CAPABILITIES.FORM_RESPONSES.VIEW,
                  [APP_MODULES.FORM_MODULE]
                )
              },
              {
                path: PAGE_URL.AUDIT_MANAGEMENT_MAP,
                element: suspenseWrapper(pages.AppAuditManagementMap)({
                  pageName: "Audit Management"
                }),
                accessValidator: authValidator.capabilityGuardValidationBuilder(
                  authValidator.CAPABILITIES.FORM_RESPONSES.VIEW,
                  [APP_MODULES.FORM_MODULE]
                )
              },
              {
                path: PAGE_URL.AUDIT_MANAGEMENT_DRAFT,
                element: suspenseWrapper(pages.AppAuditManagementDraft)({
                  pageName: "Audit Management Drafts"
                }),
                accessValidator: authValidator.capabilityGuardValidationBuilder(
                  authValidator.CAPABILITIES.FORM_RESPONSES.VIEW,
                  [APP_MODULES.FORM_MODULE]
                )
              }
            ]
          },
          {
            path: PAGE_URL.FORMS_MANAGEMENT,
            element: suspenseWrapper(pages.AppAuditManagementForms)({
              pageName: "Audit Management"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              authValidator.CAPABILITIES.FORMS.VIEW,
              [APP_MODULES.FORM_MODULE]
            ),
            children: [
              {
                path: "",
                element: suspenseWrapper(pages.AppFormManagementAllForm)({
                  pageName: "Forms Management: All forms"
                }),
                accessValidator: authValidator.capabilityGuardValidationBuilder(
                  authValidator.CAPABILITIES.FORMS.VIEW,
                  [APP_MODULES.FORM_MODULE]
                )
              },
              {
                path: PAGE_URL.FORMS_MANAGEMENT_TEAMS,
                element: suspenseWrapper(pages.AppFormManagementTeams)({
                  pageName: "Forms Management: Teams"
                }),
                accessValidator: authValidator.capabilityGuardValidationBuilder(
                  authValidator.CAPABILITIES.FORMS.VIEW,
                  [APP_MODULES.FORM_MODULE]
                )
              }
            ]
          },
          {
            path: PAGE_URL.AUDIT_MANAGEMENT_DASHBOARD,
            element: suspenseWrapper(pages.AppAuditManagementDashboard)({
              pageName: "Audit Management"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              authValidator.CAPABILITIES.FORMS.VIEW,
              [APP_MODULES.FORM_MODULE]
            )
          },
          {
            path: PAGE_URL.AUDIT_MANAGEMENT_EXPORT,
            element: suspenseWrapper(pages.AppAuditManagementExport)({
              pageName: "Audit Management Export"
            }),
            accessValidator: authValidator.capabilitiesGuardValidationBuilder(
              [
                authValidator.CAPABILITIES.FORM_RESPONSES.VIEW,
                authValidator.CAPABILITIES.FORM_RESPONSES.EXPORT
              ],
              [APP_MODULES.FORM_MODULE],
              ["operational"],
              {
                renderAccessDenied: true,
                accessDeniedMessage:
                  "You do not have permission to access this page.",
                requireAll: true
              }
            )
          },
          {
            path: PAGE_URL.AUDIT_MANAGEMENT_EXTERNAL_DATA,
            element: suspenseWrapper(pages.AppAuditManagementExternalData)({
              pageName: "Form External Data & Images"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "data_sources.view",
              [APP_MODULES.FORM_AUDIT_MODULE, APP_MODULES.FORM_MODULE]
            )
          },
          {
            path: PAGE_URL.AUDIT_MANAGEMENT_EXTERNAL_DATA_DETAIL,
            element: suspenseWrapper(pages.AppExternalDataRows)({
              pageName: "External Data"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "data_sources.view",
              [APP_MODULES.FORM_AUDIT_MODULE, APP_MODULES.FORM_MODULE]
            )
          },
          {
            path: PAGE_URL.AUDIT_MANAGEMENT_EXTERNAL_DATA_EDIT,
            element: suspenseWrapper(pages.AppExternalDataRows)({
              pageName: "External Data"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "data_sources.manage",
              [APP_MODULES.FORM_AUDIT_MODULE, APP_MODULES.FORM_MODULE]
            )
          },
          {
            path: PAGE_URL.PICTURE_MANAGEMENT,
            element: suspenseWrapper(pages.AppPictureManagement)({
              pageName: "Media Manager"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              authValidator.CAPABILITIES.MEDIA_TEMPLATES.VIEW,
              [APP_MODULES.PICTURE_MODULE]
            )
          },
          {
            path: "*",
            element: <AppErrorDispatcher errorStatusCode={404} />,
            accessValidator: authValidator.noGuardValidationBuilder()
          }
        ]
      },
      {
        path: PAGE_URL.TEMPLATE_BUILDER,
        element: suspenseWrapper(pages.AppTemplateBuilder)({
          pageName: "Template Builder"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.MEDIA_TEMPLATES.MANAGE,
          [APP_MODULES.PICTURE_MODULE]
        )
      },
      {
        path: PAGE_URL.RUN_MEDIA_CHECKLIST,
        element: suspenseWrapper(pages.AppRunMediaChecklist)({
          pageName: "Run Media Checklist"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.MEDIA_EXECUTION.EXECUTE,
          [APP_MODULES.FORM_MODULE]
        )
      },
      {
        path: `${PAGE_URL.RUN_MEDIA_CHECKLIST}/:pollAnswerId`,
        element: suspenseWrapper(pages.AppRunMediaChecklist)({
          pageName: "Run Media Checklist"
        }),
        accessValidator: authValidator.capabilitiesGuardValidationBuilder(
          [
            authValidator.CAPABILITIES.FORM_RESPONSES.VIEW,
            authValidator.CAPABILITIES.FORM_RESPONSES.MANAGE,
            authValidator.CAPABILITIES.FORM_RESPONSES.REVIEW
          ],
          [APP_MODULES.FORM_MODULE]
        )
      },
      {
        path: PAGE_URL.CATEGORY_POLL_LIST,
        element: suspenseWrapper(pages.AppCategoryPollList)({
          pageName: "Category Poll Management"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.FORMS.VIEW,
          [APP_MODULES.FORM_MODULE]
        )
      },
      {
        path: PAGE_URL.CATEGORY_POLL_NEW,
        element: suspenseWrapper(pages.AppCategoryPollNew)({
          pageName: "New Category"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.FORMS.MANAGE,
          [APP_MODULES.FORM_MODULE]
        )
      },
      {
        path: PAGE_URL.CATEGORY_POLL_UPDATE,
        element: suspenseWrapper(pages.AppCategoryPollUpdate)({
          pageName: "Update Category"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.FORMS.MANAGE,
          [APP_MODULES.FORM_MODULE]
        )
      },
      {
        path: PAGE_URL.CATEGORY_POLL_DELETE,
        element: suspenseWrapper(pages.AppCategoryPollDelete)({
          pageName: "Delete Category"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.FORMS.MANAGE,
          [APP_MODULES.FORM_MODULE]
        )
      },

      {
        path: PAGE_URL.MAINTENANCE_AUDIT_FORM,
        element: <h1>Work in Progress</h1>,
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.FORMS.VIEW,
          [APP_MODULES.FORM_MODULE]
        )
      },
      {
        path: PAGE_URL.TEAMS_MANAGEMENT,
        element: suspenseWrapper(pages.AppTeamsManagement)({
          pageName: "Team Management"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.TEAMS.VIEW,
          [APP_MODULES.WORK_ORDER_MODULE]
        )
      },
      {
        path: PAGE_URL.WORKSPACE_MANAGEMENT,
        element: suspenseWrapper(pages.AppProjectManagement)({
          pageName: "WorkspaceManagement"
        }),
        accessValidator: authValidator.workspaceGuardValidationBuilder([
          "master"
        ])
      },
      {
        path: PAGE_URL.INBOX_MANAGEMENT_LIST,
        element: suspenseWrapper(pages.AppInboxManagementList)({
          pageName: "Inbox List"
        }),
        accessValidator: authValidator.siteVisitGuardValidationBuilder()
      },
      {
        path: PAGE_URL.SETTINGS,
        element: suspenseWrapper(pages.AppSettings)({
          pageName: "Settings"
        }),
        accessValidator: authValidator.onlyPermissionUser(),
        // route settings/*, child of setting view
        children: [
          {
            path: "",
            element: suspenseWrapper(pages.AppSettingsHome)({
              pageName: "Settings"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder()
          },
          {
            path: PAGE_URL.USER_MANAGEMENT,
            element: suspenseWrapper(pages.AppUserManagement)({
              pageName: "User Management"
            }),
            // Shared: master manages global users; operational workspaces manage
            // their local Workspace Users. Backend enforces which local actions
            // are allowed per governance policies.
            accessValidator: authValidator.settingsGuardValidationBuilder([
              "master",
              "operational"
            ])
          },
          {
            path: PAGE_URL.NEW_USER,
            element: suspenseWrapper(pages.AppUserManagementNew)({
              pageName: "New User"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder([
              "master",
              "operational"
            ])
          },
          {
            path: PAGE_URL.UPDATE_USER,
            element: suspenseWrapper(pages.AppUserManagementUpdate)({
              pageName: "Update User"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder([
              "master",
              "operational"
            ])
          },
          {
            path: PAGE_URL.CHANGE_PASSWORD,
            element: suspenseWrapper(pages.AppUserManagementChangePassword)({
              pageName: "Change Password"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder([
              "master",
              "operational"
            ])
          },
          {
            path: PAGE_URL.USER_DELETE,
            element: suspenseWrapper(pages.AppUserManagementDelete)({
              pageName: "Delete User"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder([
              "master",
              "operational"
            ])
          },
          {
            path: PAGE_URL.USER_ACTIVITY,
            element: suspenseWrapper(pages.AppUserManagementActivity)({
              pageName: "User Activities"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder([
              "master",
              "operational"
            ])
          },
          {
            path: PAGE_URL.PERMISSION_PROFILE_MANAGEMENT,
            element: suspenseWrapper(pages.AppPermissionProfileManagement)({
              pageName: "Permission Profile Management"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "permission_profiles.view",
              [APP_MODULES.SETTINGS_MODULE],
              ["master"]
            )
          },
          {
            path: PAGE_URL.PERMISSION_PROFILE_NEW,
            element: suspenseWrapper(pages.AppPermissionProfile)({
              pageName: "Permission Profile New"
            }),
            accessValidator: authValidator.capabilitiesGuardValidationBuilder(
              ["permission_profiles.view", "permission_profiles.create"],
              [APP_MODULES.SETTINGS_MODULE],
              ["master"],
              {
                renderAccessDenied: true,
                requireAll: true,
                accessDeniedMessage:
                  "Creating Permission Profiles requires the Master workspace and the create permission."
              }
            )
          },
          {
            path: PAGE_URL.PERMISSION_PROFILE_UPDATE,
            element: suspenseWrapper(pages.AppPermissionProfile)({
              pageName: "Permission Profile Update"
            }),
            accessValidator: authValidator.capabilitiesGuardValidationBuilder(
              ["permission_profiles.view", "permission_profiles.update"],
              [APP_MODULES.SETTINGS_MODULE],
              ["master"],
              {
                renderAccessDenied: true,
                requireAll: true,
                accessDeniedMessage:
                  "Editing Permission Profiles requires the Master workspace and the edit permission."
              }
            )
          },
          {
            path: PAGE_URL.PERMISSION_PROFILE_VIEW,
            element: suspenseWrapper(pages.AppPermissionProfile)({
              pageName: "Permission Profile View"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "permission_profiles.view",
              [APP_MODULES.SETTINGS_MODULE],
              ["master", "operational"]
            )
          },
          {
            path: PAGE_URL.USER_COMPANY,
            element: suspenseWrapper(pages.AppUserManagementCompany)({
              pageName: "Own Company Management"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder([
              "master"
            ])
          },
          {
            path: PAGE_URL.USER_COMPANY_NEW,
            element: suspenseWrapper(pages.AppUserManagementCompanyNew)({
              pageName: "New Own Company"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder([
              "master"
            ])
          },
          {
            path: PAGE_URL.USER_COMPANY_UPDATE,
            element: suspenseWrapper(pages.AppUserManagementCompanyUpdate)({
              pageName: "Update Own Company"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder([
              "master"
            ])
          },
          {
            path: PAGE_URL.USER_COMPANY_DELETE,
            element: suspenseWrapper(pages.AppUserManagementCompanyDelete)({
              pageName: "Delete Own Company"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder([
              "master"
            ])
          },
          {
            path: PAGE_URL.USER_SECURITY,
            element: suspenseWrapper(pages.AppUserManagementSecurity)({
              pageName: "User Security"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder([
              "master"
            ])
          },
          {
            path: PAGE_URL.SETTINGS_COMPANY,
            element: suspenseWrapper(pages.AppSettingsCompany)({
              pageName: "Organization Management"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder([
              "master"
            ])
          },
          {
            path: PAGE_URL.SETTINGS_AUDIT_LOG,
            element: suspenseWrapper(pages.AppAuditCenter)({
              pageName: "Audit Log"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "audit_log.view",
              [APP_MODULES.SETTINGS_MODULE],
              ["master"]
            )
          },
          {
            path: PAGE_URL.SETTINGS_NOTIFICATIONS,
            element: suspenseWrapper(pages.AppSettingsNotifications)({
              pageName: "Notifications"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "notifications.manage_settings",
              [APP_MODULES.MESSAGE_MODULE],
              ["master"]
            )
          },
          {
            path: PAGE_URL.LAYOUT_DASHBOARD_MANAGEMENT,
            element: suspenseWrapper(pages.AppLayoutDashboardManagement)({
              pageName: "Dashboard Management"
            }),
            accessValidator: masterDashboardManageGuard
          },
          {
            path: PAGE_URL.LAYOUT_DASHBOARD_UPDATE,
            element: suspenseWrapper(pages.AppLayoutDashboardUpdate)({
              pageName: "Update Dashboard"
            }),
            accessValidator: masterDashboardManageGuard
          },
          {
            path: PAGE_URL.LAYOUT_DASHBOARD_NEW,
            element: suspenseWrapper(pages.AppLayoutDashboardNew)({
              pageName: "New Dashboard"
            }),
            accessValidator: masterDashboardManageGuard
          },
          {
            path: PAGE_URL.LAYOUT_DASHBOARD_DELETE,
            element: suspenseWrapper(pages.AppLayoutDashboardDelete)({
              pageName: "Delete Dashboard"
            }),
            accessValidator: masterDashboardManageGuard
          },
          {
            path: PAGE_URL.REPORT_TEMPLATE_LIST,
            element: suspenseWrapper(pages.AppReportTemplateManagement)({
              pageName: "Report Manager"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "report_templates.manage",
              [APP_MODULES.REPORTS_MODULE],
              ["master"]
            )
          },
          {
            path: PAGE_URL.SETTINGS_APP,
            // TODO: Change for App page component
            element: suspenseWrapper(pages.AppSettingsOthers)({
              pageName: "App Settings"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder()
          },
          {
            path: PAGE_URL.SETTINGS_APP_PHOTO_QUALITY,
            element: suspenseWrapper(pages.AppSettingsPhotoQuality)({
              pageName: "App Settings"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder()
          },
          {
            path: PAGE_URL.SETTINGS_TIMESHEETS,
            element: suspenseWrapper(pages.AppSettingsTimesheets)({
              pageName: "Timesheets Settings"
            }),
            accessValidator:
              authValidator.timesheetSettingsGuardValidationBuilder()
          },
          {
            path: PAGE_URL.SETTINGS_TIMESHEETS_SECTION,
            element: suspenseWrapper(pages.AppSettingsTimesheets)({
              pageName: "Timesheets Settings"
            }),
            accessValidator:
              authValidator.timesheetSettingsGuardValidationBuilder()
          },
          {
            path: PAGE_URL.SETTINGS_SCHEDULER,
            element: suspenseWrapper(pages.AppSettingsScheduler)({
              pageName: "Scheduler Settings"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "scheduler.manage_settings",
              [APP_MODULES.WORK_ORDER_MODULE]
            )
          },
          {
            path: PAGE_URL.SETTINGS_MAINTENANCE,
            element: suspenseWrapper(pages.AppSettingsMaintenance)({
              pageName: "Maintenance Settings"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "maintenance.manage_settings",
              [APP_MODULES.WORK_ORDER_MODULE]
            )
          },
          {
            path: PAGE_URL.SETTINGS_APPROVAL_FLOWS,
            element: suspenseWrapper(pages.AppSettingsApprovalFlows)({
              pageName: "Approval flows"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              authValidator.CAPABILITIES.APPROVAL_FLOWS.VIEW,
              [APP_MODULES.WORK_ORDER_MODULE]
            )
          },
          {
            path: PAGE_URL.SETTINGS_APPROVAL_FLOW_NEW,
            element: suspenseWrapper(pages.AppSettingsApprovalFlows)({
              pageName: "Approval flows"
            }),
            accessValidator: authValidator.capabilitiesGuardValidationBuilder(
              [authValidator.CAPABILITIES.APPROVAL_FLOWS.MANAGE],
              [APP_MODULES.WORK_ORDER_MODULE],
              ["operational"],
              {
                renderAccessDenied: true,
                accessDeniedMessage:
                  "You do not have permission to access this page.",
                alsoRequire: [authValidator.CAPABILITIES.APPROVAL_FLOWS.VIEW]
              }
            )
          },
          {
            path: PAGE_URL.SETTINGS_APPROVAL_FLOW_EDIT,
            element: suspenseWrapper(pages.AppSettingsApprovalFlows)({
              pageName: "Approval flows"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              authValidator.CAPABILITIES.APPROVAL_FLOWS.VIEW,
              [APP_MODULES.WORK_ORDER_MODULE]
            )
          },
          {
            path: PAGE_URL.SETTINGS_UNPLANNED_WO,
            element: suspenseWrapper(pages.AppSettingsUnplannedWO)({
              pageName: "Unplanned WO Settings"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder([
              "operational"
            ])
          },
          {
            path: PAGE_URL.SETTINGS_INVENTORY,
            element: suspenseWrapper(pages.AppSettingsInventory)({
              pageName: "Inventory Settings"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder()
          },
          {
            path: PAGE_URL.SETTINGS_INVENTORY_CATALOG,
            element: suspenseWrapper(pages.AppSettingsInventory)({
              pageName: "Inventory Settings"
            }),
            accessValidator:
              authValidator.settingsPermissionGuardValidationBuilder(
                [authValidator.MODULE_PERMISSIONS.INVENTORY.manageCatalog],
                ["master"]
              )
          },
          {
            path: PAGE_URL.SETTINGS_INVENTORY_WAREHOUSES,
            element: suspenseWrapper(pages.AppSettingsInventory)({
              pageName: "Inventory Settings"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder([
              "operational"
            ])
          },
          {
            path: PAGE_URL.SETTINGS_INVENTORY_JOB_TYPES,
            element: suspenseWrapper(pages.AppSettingsInventory)({
              pageName: "Inventory Settings"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder([
              "operational"
            ])
          },
          {
            path: PAGE_URL.SETTINGS_DOCUMENT_TYPES,
            element: suspenseWrapper(pages.AppSettingsDocumentTypes)({
              pageName: "Document Types Settings"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "document_types.manage",
              [APP_MODULES.DOCUMENTS_MODULE],
              ["master"]
            )
          },
          {
            path: PAGE_URL.SETTINGS_DEMO_DATA,
            element: suspenseWrapper(pages.AppSettingsDemoData)({
              pageName: "Data Demo Settings"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder([
              "master"
            ])
          },
          {
            path: PAGE_URL.SETTINGS_TRACKER_TEMPLATES,
            element: suspenseWrapper(pages.AppTrackerTemplates)({
              pageName: "Tracker Templates Settings"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "tracker.manage_templates",
              [APP_MODULES.OPERATIONAL_TRACKER_MODULE],
              ["master"]
            )
          },
          {
            path: PAGE_URL.SETTINGS_OTHERS,
            element: suspenseWrapper(pages.AppSettingsOthers)({
              pageName: "Other Settings"
            }),
            accessValidator: authValidator.settingsGuardValidationBuilder([
              "operational"
            ])
          },
          {
            path: PAGE_URL.SETTINGS_JOB_TYPE_TASKS,
            element: suspenseWrapper(pages.AppSettingsJobTypeTasks)({
              pageName: "Job Type Settings"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              authValidator.CAPABILITIES.JOB_TYPES.MANAGE,
              [APP_MODULES.WORK_ORDER_MODULE],
              ["master"]
            )
          },
          {
            path: PAGE_URL.SETTINGS_JOB_TYPE_CUSTOM_FIELDS,
            element: suspenseWrapper(pages.AppSettingsJobTypeCustomFields)({
              pageName: "Job Type Custom Fields"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              authValidator.CAPABILITIES.JOB_TYPES.MANAGE,
              [APP_MODULES.WORK_ORDER_MODULE],
              ["master"]
            )
          },
          {
            path: PAGE_URL.SETTINGS_JOB_TYPE_MICRO_REPORT,
            element: suspenseWrapper(pages.AppSettingsJobType)({
              pageName: "Job Type Micro Report"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              authValidator.CAPABILITIES.JOB_TYPES.MANAGE,
              [APP_MODULES.WORK_ORDER_MODULE],
              ["master"]
            )
          }
        ]
      },
      {
        path: PAGE_URL.USER_PROFILE_HOME,
        element: suspenseWrapper(pages.AppUserProfileHome)({
          pageName: "User Profile Home"
        }),
        accessValidator: authValidator.noGuardValidationBuilder(),
        children: [
          {
            path: PAGE_URL.MY_PROFILE,
            element: suspenseWrapper(pages.AppUserProfile)({
              pageName: "User Profile"
            }),
            accessValidator: authValidator.noGuardValidationBuilder()
          },
          {
            path: PAGE_URL.NOTIFICATIONS_PREFERENCES,
            element: suspenseWrapper(pages.AppNotificationsPreferences)({
              pageName: "Notifications Preferences"
            }),
            accessValidator: authValidator.noGuardValidationBuilder()
          }
        ]
      },
      {
        path: PAGE_URL.USER_BUSINESS,
        element: suspenseWrapper(pages.AppUserManagementBusiness)({
          pageName: "Business Management"
        }),
        accessValidator: authValidator.settingsGuardValidationBuilder([
          "master",
          "operational"
        ])
      },
      {
        path: PAGE_URL.USER_BUSINESS_NEW,
        element: suspenseWrapper(pages.AppUserManagementBusinessNew)({
          pageName: "New Business"
        }),
        accessValidator: authValidator.settingsGuardValidationBuilder([
          "master",
          "operational"
        ])
      },
      {
        path: PAGE_URL.USER_BUSINESS_UPDATE,
        element: suspenseWrapper(pages.AppUserManagementBusinessUpdate)({
          pageName: "Update Business"
        }),
        accessValidator: authValidator.settingsGuardValidationBuilder([
          "master",
          "operational"
        ])
      },
      {
        path: PAGE_URL.USER_BUSINESS_DELETE,
        element: suspenseWrapper(pages.AppUserManagementBusinessDelete)({
          pageName: "Delete Business"
        }),
        accessValidator: authValidator.settingsGuardValidationBuilder([
          "master",
          "operational"
        ])
      },
      {
        path: PAGE_URL.EQUIPMENTS_MANAGEMENT,
        element: suspenseWrapper(pages.AppEquipmentManagement)({
          pageName: "Equipments Management"
        }),
        accessValidator: authValidator.siteVisitGuardValidationBuilder([
          "operational"
        ]),
        children: [
          {
            path: PAGE_URL.EQUIPMENTS_MANAGEMENT_STOCK_CONTROL,
            element: suspenseWrapper(pages.AppEquipmentManagementStockControl)({
              pageName: "Stock Control"
            }),
            accessValidator: authValidator.siteVisitGuardValidationBuilder([
              "operational"
            ])
          }
        ]
      },
      {
        path: PAGE_URL.INVENTORY,
        element: suspenseWrapper(pages.AppInventory)({
          pageName: "Inventory"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.INVENTORY.VIEW,
          [APP_MODULES.INVENTORY_MODULE],
          ["operational"]
        )
      },
      {
        path: PAGE_URL.MAP,
        element: suspenseWrapper(pages.AppSettingsMap)({
          pageName: "Settings Map"
        }),
        accessValidator: authValidator.settingsGuardValidationBuilder([
          "master",
          "operational"
        ])
      },
      {
        path: PAGE_URL.SITE_VISITS,
        element: suspenseWrapper(pages.AppSiteVisits)({
          pageName: "Site Visits"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          "visits.view",
          [APP_MODULES.SITE_VISIT_MODULE]
        ),
        children: [
          {
            path: PAGE_URL.SITE_VISITS_DASHBOARD,
            element: suspenseWrapper(pages.AppSiteVisitsDashboard)({
              pageName: "Site Visits|Dashboard"
            }),
            accessValidator: authValidator.siteVisitGuardValidationBuilder([
              "operational"
            ])
          },
          {
            path: PAGE_URL.SITE_VISITS_LIST,
            element: suspenseWrapper(pages.AppSiteVisitsDetails)({
              pageName: "Site Visits|Details"
            }),
            accessValidator: authValidator.siteVisitGuardValidationBuilder([
              "operational"
            ])
          },
          {
            path: PAGE_URL.SITE_VISITS_WORK_IN_PROGRESS,
            element: suspenseWrapper(pages.AppSiteVisitsWorkInProgress)({
              pageName: "Site Visits|Work in progress"
            }),
            accessValidator: authValidator.siteVisitGuardValidationBuilder([
              "operational"
            ])
          },
          {
            path: PAGE_URL.SITE_VISITS_TRACKER_LIST,
            element: suspenseWrapper(pages.AppSiteVisitsLive)({
              pageName: "Site | Real field visits"
            }),
            accessValidator: authValidator.siteVisitGuardValidationBuilder([
              "operational"
            ])
          },
          {
            path: PAGE_URL.SITE_VISITS_REAL_TIME_LIST,
            element: suspenseWrapper(pages.AppSiteVisitsLiveView)({
              pageName: "Site Visits|Real time tracker"
            }),
            accessValidator: authValidator.siteVisitGuardValidationBuilder([
              "operational"
            ])
          },
          {
            path: PAGE_URL.SITE_VISITS_BROADCASTER,
            element: suspenseWrapper(pages.AppInboxManagementList)({
              pageName: "Site Visits|Inbox"
            }),
            accessValidator: authValidator.siteVisitGuardValidationBuilder([
              "operational"
            ])
          }
        ]
      },
      {
        path: PAGE_URL.GROUP_REALTIME_NEW,
        element: suspenseWrapper(pages.AppSiteVisitsGroupRealtimeNew)({
          pageName: "Realtime Group"
        }),
        accessValidator: authValidator.siteVisitGuardValidationBuilder([
          "operational"
        ])
      },
      {
        path: PAGE_URL.GROUP_REALTIME_UPDATE,
        element: suspenseWrapper(pages.AppSiteVisitsGroupRealtimeUpdate)({
          pageName: "Realtime Group"
        }),
        accessValidator: authValidator.siteVisitGuardValidationBuilder([
          "operational"
        ])
      },
      {
        path: PAGE_URL.MODEL_VIEWS,
        element: suspenseWrapper(pages.AppAssetItemsList)({
          pageName: "App Model View"
        }),
        accessValidator: authValidator.assetCapabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.ASSETS.VIEW
        )
      },
      {
        path: PAGE_URL.ENTITY_DETAIL,
        element: suspenseWrapper(pages.AppEntityDetail)({
          pageName: "App Entity Detail"
        }),
        accessValidator: assetDetailAccessValidator
      },
      ...(ENABLE_ASSET_PHOTO_GALLERY
        ? [
            {
              path: PAGE_URL.ASSET_PHOTO_GALLERY,
              element: suspenseWrapper(pages.AppAssetPhotoGallery)({
                pageName: "Asset Photo Gallery"
              }),
              accessValidator: assetPhotoGalleryAccessValidator
            },
            {
              path: PAGE_URL.ASSET_PHOTO_GALLERY_HISTORY,
              element: suspenseWrapper(pages.AppAssetPhotoGallery)({
                pageName: "Asset Photo Gallery History"
              }),
              accessValidator: assetPhotoGalleryAccessValidator
            }
          ]
        : []),

      {
        path: PAGE_URL.NETWORK_ELEMENT,
        element: suspenseWrapper(pages.AppNetworkElement)({
          pageName: "Network Element"
        }),
        accessValidator: authValidator.assetCapabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.ASSETS.VIEW
        ),
        children: []
      },
      {
        path: PAGE_URL.NETWORK_ELEMENT_FORM_BEGIN,
        element: suspenseWrapper(pages.AppNetworkElementFormBegin)({
          pageName: "Network Element Begin"
        }),
        accessValidator: authValidator.assetCapabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.ASSETS.MANAGE
        )
      },

      {
        path: PAGE_URL.NETWORK_ELEMENT_FORM_SYSTEM_CARDS,
        element: suspenseWrapper(pages.AppNetworkElementFormSystem)({
          pageName: "Network Element System"
        }),
        accessValidator: authValidator.assetCapabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.ASSETS.MANAGE
        )
      },
      {
        path: PAGE_URL.NETWORK_ELEMENT_FORM_MODULES,
        element: suspenseWrapper(pages.AppNetworkElementFormModules)({
          pageName: "Network Element Modules"
        }),
        accessValidator: authValidator.assetCapabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.ASSETS.MANAGE
        )
      },
      {
        path: PAGE_URL.NETWORK_ELEMENT_DETAILS,
        element: suspenseWrapper(pages.AppNewModelsList)({
          pageName: "Network Element Details"
        }),
        accessValidator: authValidator.assetCapabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.ASSETS.VIEW
        )
      },
      {
        path: PAGE_URL.NETWORK_ELEMENT_FORM_FINISH,
        element: suspenseWrapper(pages.AppNetworkElementFormFinish)({
          pageName: "Network Element Begin"
        }),
        accessValidator: authValidator.assetCapabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.ASSETS.MANAGE
        )
      },
      {
        path: PAGE_URL.WORK_ORDER,
        element: suspenseWrapper(pages.AppWorkOrder)({
          pageName: "Work Order"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.WORK_ORDERS.VIEW,
          [APP_MODULES.WORK_ORDER_MODULE],
          ["operational"]
        )
      },
      {
        // FE #3425: the backend decides who may see each unit and resolve;
        // the route only requires access to work orders.
        path: PAGE_URL.WORK_ORDER_SERIAL_CONFLICT,
        element: suspenseWrapper(pages.AppSerialConflict)({
          pageName: "Serial conflict"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.WORK_ORDERS.VIEW,
          [APP_MODULES.WORK_ORDER_MODULE],
          ["operational"]
        )
      },
      {
        path: PAGE_URL.WORK_ORDER_TEMPLATES,
        element: suspenseWrapper(pages.AppWorkOrderTemplates)({
          pageName: "Work Order Template"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.WORK_ORDERS.VIEW,
          [APP_MODULES.WORK_ORDER_MODULE],
          ["master"]
        )
      },
      {
        path: PAGE_URL.MAINTENANCE,
        element: suspenseWrapper(pages.AppMaintenance)({
          pageName: "Maintenance"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.MAINTENANCE.VIEW,
          [APP_MODULES.WORK_ORDER_MODULE]
        )
      },
      {
        path: PAGE_URL.MAINTENANCE_NEW,
        element: suspenseWrapper(pages.AppMaintenance)({
          pageName: "Maintenance"
        }),
        accessValidator: authValidator.capabilitiesGuardValidationBuilder(
          [
            authValidator.CAPABILITIES.MAINTENANCE.VIEW,
            authValidator.CAPABILITIES.MAINTENANCE.CREATE_PLAN
          ],
          [APP_MODULES.WORK_ORDER_MODULE],
          ["operational"],
          {
            requireAll: true,
            renderAccessDenied: true,
            accessDeniedMessage:
              "You do not have permission to access this page."
          }
        )
      },
      {
        path: PAGE_URL.MAINTENANCE_EDIT,
        element: suspenseWrapper(pages.AppMaintenance)({
          pageName: "Maintenance"
        }),
        accessValidator: authValidator.capabilitiesGuardValidationBuilder(
          [
            authValidator.CAPABILITIES.MAINTENANCE.VIEW,
            authValidator.CAPABILITIES.MAINTENANCE.UPDATE_PLAN
          ],
          [APP_MODULES.WORK_ORDER_MODULE],
          ["operational"],
          {
            requireAll: true,
            renderAccessDenied: true,
            accessDeniedMessage:
              "You do not have permission to access this page."
          }
        )
      },
      {
        path: PAGE_URL.MAINTENANCE_RUNS,
        element: suspenseWrapper(pages.AppMaintenance)({
          pageName: "Maintenance"
        }),
        accessValidator: authValidator.capabilitiesGuardValidationBuilder(
          [
            authValidator.CAPABILITIES.MAINTENANCE.VIEW,
            authValidator.CAPABILITIES.MAINTENANCE.VIEW_HISTORY
          ],
          [APP_MODULES.WORK_ORDER_MODULE],
          ["operational"],
          {
            requireAll: true,
            renderAccessDenied: true,
            accessDeniedMessage:
              "You do not have permission to access this page."
          }
        )
      },
      {
        path: PAGE_URL.MAINTENANCE_DETAIL,
        element: suspenseWrapper(pages.AppMaintenance)({
          pageName: "Maintenance"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.MAINTENANCE.VIEW,
          [APP_MODULES.WORK_ORDER_MODULE]
        )
      },
      {
        path: PAGE_URL.SCHEDULER,
        element: suspenseWrapper(pages.AppSchedulerRoot)({
          pageName: "Scheduler"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.SCHEDULER.VIEW,
          [APP_MODULES.WORK_ORDER_MODULE]
        ),
        children: [
          {
            path: PAGE_URL.SCHEDULER_DASHBOARD,
            element: suspenseWrapper(pages.AppScheduler)({
              pageName: "Scheduler Dashboard"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              authValidator.CAPABILITIES.SCHEDULER.VIEW,
              [APP_MODULES.WORK_ORDER_MODULE]
            )
          },
          {
            path: PAGE_URL.TECHNICIAN_LIST,
            element: suspenseWrapper(pages.AppSchedulerTechnicians)({
              pageName: "Scheduler Technicians"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              authValidator.CAPABILITIES.SCHEDULER.VIEW,
              [APP_MODULES.WORK_ORDER_MODULE]
            )
          },
          {
            path: "*",
            loader: () => {
              return redirect(PAGE_URL.SCHEDULER_DASHBOARD);
            },
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              authValidator.CAPABILITIES.SCHEDULER.VIEW,
              [APP_MODULES.WORK_ORDER_MODULE]
            )
          }
        ]
      },
      {
        path: PAGE_URL.TIMESHEETS,
        element: suspenseWrapper(pages.AppTimesheets)({
          pageName: "Timesheets"
        }),
        accessValidator: authValidator.timesheetGuardValidationBuilder()
      },
      {
        path: PAGE_URL.APPROVALS_INBOX,
        element: suspenseWrapper(pages.AppWorkOrderApprovals)({
          pageName: "Approvals"
        }),
        // work_orders.view plus any approval capability (§J.2); the backend
        // applies the same gate and the per-object eligibility.
        accessValidator: authValidator.capabilitiesGuardValidationBuilder(
          [
            authValidator.CAPABILITIES.WORK_ORDER_APPROVALS.VIEW,
            authValidator.CAPABILITIES.WORK_ORDER_APPROVALS.REVIEW_REPORT,
            authValidator.CAPABILITIES.WORK_ORDER_APPROVALS.REVIEW_INVENTORY
          ],
          [APP_MODULES.WORK_ORDER_MODULE],
          ["operational"],
          {
            renderAccessDenied: true,
            accessDeniedMessage:
              "You do not have permission to access this page.",
            alsoRequire: [authValidator.CAPABILITIES.WORK_ORDERS.VIEW]
          }
        )
      },
      {
        path: PAGE_URL.OPERATIONS_OVERVIEW,
        element: suspenseWrapper(pages.AppOperationsOverview)({
          pageName: "Operations Overview"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.WORK_ORDERS.VIEW,
          [APP_MODULES.WORK_ORDER_MODULE]
        )
      },
      {
        path: PAGE_URL.PROJECTS,
        element: suspenseWrapper(pages.AppProjects)({
          pageName: "Projects"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          "tracker.view",
          [APP_MODULES.OPERATIONAL_TRACKER_MODULE]
        )
      },
      {
        path: PAGE_URL.PROJECTS_CREATE,
        element: suspenseWrapper(pages.AppProjectsCreate)({
          pageName: "Create Project"
        }),
        accessValidator: authValidator.capabilitiesGuardValidationBuilder(
          ["tracker.view", "tracker.create", "tracker.manage_templates"],
          [APP_MODULES.OPERATIONAL_TRACKER_MODULE],
          ["operational"],
          { requireAll: true, renderAccessDenied: true }
        )
      },
      {
        path: PAGE_URL.PROJECTS_UPDATE,
        element: suspenseWrapper(pages.AppProjectsCreate)({
          pageName: "Update Project"
        }),
        accessValidator: authValidator.capabilitiesGuardValidationBuilder(
          ["tracker.view", "tracker.update", "tracker.manage_templates"],
          [APP_MODULES.OPERATIONAL_TRACKER_MODULE],
          ["operational"],
          { requireAll: true, renderAccessDenied: true }
        )
      },
      {
        path: PAGE_URL.PROJECTS_DETAIL,
        element: suspenseWrapper(pages.AppProjectsDetail)({
          pageName: "Project Detail"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          "tracker.view",
          [APP_MODULES.OPERATIONAL_TRACKER_MODULE]
        )
      },
      {
        path: PAGE_URL.DOCUMENTS,
        element: suspenseWrapper(pages.AppDocuments)({
          pageName: "Documents"
        }),
        accessValidator: authValidator.documentsGuardValidationBuilder()
      },
      {
        path: PAGE_URL.ASSET_SEARCH,
        element: suspenseWrapper(pages.AppAssetSearch)({
          pageName: "Asset Search"
        }),
        accessValidator: authValidator.assetCapabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.ASSET_SEARCH.RUN
        )
      },
      {
        path: PAGE_URL.ASSET_SEARCH_SAVED,
        element: suspenseWrapper(pages.AppAssetSearch)({
          pageName: "Asset Search"
        }),
        accessValidator: authValidator.assetCapabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.ASSET_SEARCH.SAVED_VIEW
        )
      },
      {
        path: PAGE_URL.FORM_ANALYSIS,
        element: suspenseWrapper(pages.AppFormAnalysis)({
          pageName: "Form Analysis"
        }),
        accessValidator: authValidator.formAnalysisGuardValidationBuilder()
      },
      {
        path: PAGE_URL.NETWORK_ELEMENT_PAGEBUILDER_NEW,
        element: suspenseWrapper(pages.AppNewPageBuilder)({
          pageName: "New Pagebuilder"
        }),
        accessValidator: getAssetMutationRouteAccessValidator(
          PAGE_URL.NETWORK_ELEMENT_PAGEBUILDER_NEW
        )
      },
      {
        path: PAGE_URL.NETWORK_ELEMENT_PAGEBUILDER_EDIT,
        element: suspenseWrapper(pages.AppNewPageBuilder)({
          pageName: "Edit Pagebuilder"
        }),
        accessValidator: getAssetMutationRouteAccessValidator(
          PAGE_URL.NETWORK_ELEMENT_PAGEBUILDER_EDIT
        )
      },
      {
        path: PAGE_URL.NETWORK_ELEMENT_FORMBUILDER_NEW,
        element: suspenseWrapper(pages.AppNetworkFormBuilder)({
          pageName: "New FormBuilder"
        }),
        accessValidator: getAssetMutationRouteAccessValidator(
          PAGE_URL.NETWORK_ELEMENT_FORMBUILDER_NEW
        )
      },
      {
        path: PAGE_URL.NETWORK_ELEMENT_FORMBUILDER_NEW_EDIT,
        element: suspenseWrapper(pages.AppNetworkFormBuilder)({
          pageName: "New FormBuilder Edit"
        }),
        accessValidator: getAssetMutationRouteAccessValidator(
          PAGE_URL.NETWORK_ELEMENT_FORMBUILDER_NEW_EDIT
        )
      },
      {
        path: PAGE_URL.NETWORK_ELEMENT_FORM_NEW,
        element: suspenseWrapper(pages.AppNetworkForm)({
          pageName: "New Form Create"
        }),
        accessValidator: getAssetMutationRouteAccessValidator(
          PAGE_URL.NETWORK_ELEMENT_FORM_NEW
        )
      },
      {
        path: PAGE_URL.NETWORK_ELEMENT_FORM_NEW_EDIT,
        element: suspenseWrapper(pages.AppNetworkForm)({
          pageName: "New Form Edit"
        }),
        accessValidator: getAssetMutationRouteAccessValidator(
          PAGE_URL.NETWORK_ELEMENT_FORM_NEW_EDIT
        )
      },
      {
        path: PAGE_URL.NETWORK_ELEMENT_LIST,
        element: suspenseWrapper(pages.AppNewNetworkElementList)({
          pageName: "Network Element"
        }),
        accessValidator: authValidator.assetCapabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.ASSETS.VIEW
        )
      },
      {
        path: PAGE_URL.NOTIFICATIONS,
        element: suspenseWrapper(pages.AppMessageNotification)({
          pageName: "Notifications"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          "notifications.view",
          [APP_MODULES.MESSAGE_MODULE],
          ["master", "operational"]
        )
      },
      {
        path: PAGE_URL.NOTIFICATIONS_DETAIL,
        element: suspenseWrapper(pages.AppMessageNotification)({
          pageName: "Notifications"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          "notifications.view",
          [APP_MODULES.MESSAGE_MODULE],
          ["master", "operational"]
        )
      },
      {
        path: "/lab-test",
        element: suspenseWrapper(pages.AppPageTest)({
          pageName: "Test"
        }),
        accessValidator: authValidator.noGuardValidationBuilder()
      },
      {
        path: PAGE_URL.BULK_OPERATIONS,
        element: suspenseWrapper(pages.AppBulkOperations)({
          pageName: "Bulk Operations"
        }),
        accessValidator: authValidator.capabilityGuardValidationBuilder(
          authValidator.CAPABILITIES.BULK_OPERATIONS.VIEW,
          [APP_MODULES.WORK_ORDER_MODULE]
        ),
        children: [
          {
            path: PAGE_URL.BULK_OPERATIONS_DASHBOARD,
            element: suspenseWrapper(pages.AppBulkOperationsDashboard)({
              pageName: "Bulk Operations"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              authValidator.CAPABILITIES.BULK_OPERATIONS.VIEW,
              [APP_MODULES.WORK_ORDER_MODULE]
            )
          },
          {
            path: PAGE_URL.BULK_OPERATIONS_PROCESS,
            element: suspenseWrapper(pages.AppBulkOperationsProcess)({
              pageName: "Bulk Operations Process"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              authValidator.CAPABILITIES.BULK_OPERATIONS.VIEW,
              [APP_MODULES.WORK_ORDER_MODULE]
            )
          },
          {
            path: PAGE_URL.BULK_OPERATIONS_NEW,
            element: suspenseWrapper(pages.AppBulkOperationsNew)({
              pageName: "New Bulk Operations"
            }),
            accessValidator: authValidator.capabilitiesGuardValidationBuilder(
              [
                authValidator.CAPABILITIES.BULK_OPERATIONS.VIEW,
                authValidator.CAPABILITIES.WORK_ORDERS.CREATE
              ],
              [APP_MODULES.WORK_ORDER_MODULE],
              ["operational"],
              {
                renderAccessDenied: true,
                accessDeniedMessage:
                  "You do not have permission to access this page.",
                requireAll: true
              }
            )
          }
        ]
      },
      {
        path: PAGE_URL.LIBRARY,
        element: suspenseWrapper(pages.AppLibrary)({
          pageName: "Library"
        }),
        accessValidator: authValidator.onlyMasterGuardValidationBuilder(),
        children: [
          {
            path: PAGE_URL.LIBRARY_FORMS,
            element: suspenseWrapper(pages.AppLibraryTemplates)({
              pageName: "Library Templates"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "forms.view",
              [APP_MODULES.FORM_MODULE],
              ["master"]
            ),
            children: [
              {
                path: "",
                element: suspenseWrapper(pages.AppFormManagementAllForm)({
                  pageName: "Library Templates"
                }),
                accessValidator:
                  authValidator.onlyMasterGuardValidationBuilder()
              }
            ]
          },
          {
            path: PAGE_URL.LIBRARY_FORMS_NEW,
            element: suspenseWrapper(pages.AppPollBuilderV2)({
              pageName: "Library Templates"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "forms.create",
              [APP_MODULES.FORM_MODULE],
              ["master"]
            )
          },
          {
            path: PAGE_URL.LIBRARY_FORMS_DETAIL,
            element: suspenseWrapper(pages.AppPollBuilderV2)({
              pageName: "Library Templates"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "forms.update",
              [APP_MODULES.FORM_MODULE],
              ["master"]
            )
          },
          {
            path: PAGE_URL.LIBRARY_FORMS_IMPORT,
            element: suspenseWrapper(pages.AppPollImport)({
              pageName: "Library Templates"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "forms.create",
              [APP_MODULES.FORM_MODULE],
              ["master"]
            )
          },
          {
            path: PAGE_URL.LIBRARY_EXTERNAL_DATA,
            element: suspenseWrapper(pages.AppAuditManagementExternalData)({
              pageName: "Library Templates"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "data_sources.view",
              [APP_MODULES.FORM_MODULE],
              ["master"]
            )
          },
          {
            path: PAGE_URL.LIBRARY_EXTERNAL_DATA_DETAIL,
            element: suspenseWrapper(pages.AppExternalDataRows)({
              pageName: "Library Templates"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "data_sources.view",
              [APP_MODULES.FORM_MODULE],
              ["master"]
            )
          },
          {
            path: PAGE_URL.LIBRARY_EXTERNAL_DATA_EDIT,
            element: suspenseWrapper(pages.AppExternalDataRows)({
              pageName: "Library Templates"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "data_sources.manage",
              [APP_MODULES.FORM_MODULE],
              ["master"]
            )
          },
          {
            path: PAGE_URL.LIBRARY_SCHEMAS,
            element: suspenseWrapper(pages.AppLibrarySchemas)({
              pageName: "Library Schemas"
            }),
            accessValidator: authValidator.onlyMasterGuardValidationBuilder()
          },
          {
            path: PAGE_URL.LIBRARY_SCHEMAS_NEW,
            element: suspenseWrapper(pages.AppNetworkFormBuilder)({
              pageName: "Library Schemas"
            }),
            accessValidator: authValidator.onlyMasterGuardValidationBuilder()
          },
          {
            path: PAGE_URL.LIBRARY_SCHEMAS_DETAIL,
            element: suspenseWrapper(pages.AppNetworkFormBuilder)({
              pageName: "Library Schemas"
            }),
            accessValidator: authValidator.onlyMasterGuardValidationBuilder()
          },
          {
            path: PAGE_URL.LIBRARY_MEDIA,
            element: suspenseWrapper(pages.AppLibraryMediaTemplates)({
              pageName: "Library Media Templates"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "media_templates.view",
              [APP_MODULES.PICTURE_MODULE],
              ["master"]
            )
          },
          {
            path: PAGE_URL.LIBRARY_MEDIA_NEW,
            element: suspenseWrapper(pages.AppTemplateBuilder)({
              pageName: "Library Media Templates"
            }),
            accessValidator: authValidator.capabilityGuardValidationBuilder(
              "media_templates.manage",
              [APP_MODULES.PICTURE_MODULE],
              ["master"]
            )
          }
        ]
      },
      {
        path: "*",
        element: <AppErrorDispatcher errorStatusCode={404} />,
        accessValidator: authValidator.noGuardValidationBuilder()
      }
    ]
  }
];

export default routes;
